import React, { useMemo, useCallback, useState } from 'react';
import {
  useTableFilterSortSearchManager,
  useTableManageColumns,
  useTableFilterSortData,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatPercentage } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import { getHeaders, getPropertyPathMap } from './constantsV2';
import {
  calculateSumOfEachField,
  mapValuesByField,
  weightedAverage,
  extractUniqueDevelopmentPhases,
  extractUniquePropertyTypes,
} from './utils';
import * as api from '../../utils/api';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useIntl from '../../hooks/useIntl';
import { processTableFilterOptions } from '../../utils/table-helpers';
import camelCase from 'lodash/camelCase';
import usePortfoliosQuery from './usePortfoliosQuery';

const NAME = 'portfolioOverview';

const buildColumnOrder = (columnIds = []) => {
  return columnIds.reduce((columnOrder, columnId, index) => {
    columnOrder[columnId] = index;
    return columnOrder;
  }, {});
};

const getStoredColumnOrderState = ({ headers, name }) => {
  const headerIds = headers.map(({ id }) => id);
  const defaultColumnOrder = buildColumnOrder(headerIds);

  try {
    const storedOrder = localStorage.getItem(`${name}_column-order`);
    if (!storedOrder) {
      return {
        initialColumnsOrder: defaultColumnOrder,
        shouldForceOverwrite: true,
      };
    }

    const parsedOrder = JSON.parse(storedOrder);
    const existingColumnIds = headerIds
      .filter((headerId) => parsedOrder[headerId] !== undefined)
      .sort((leftId, rightId) => parsedOrder[leftId] - parsedOrder[rightId]);
    const missingColumnIds = headerIds.filter(
      (headerId) => parsedOrder[headerId] === undefined,
    );

    if (!missingColumnIds.length) {
      return {
        initialColumnsOrder: defaultColumnOrder,
        shouldForceOverwrite: false,
      };
    }

    return {
      initialColumnsOrder: buildColumnOrder([
        ...existingColumnIds,
        ...missingColumnIds,
      ]),
      shouldForceOverwrite: true,
    };
  } catch {
    return {
      initialColumnsOrder: defaultColumnOrder,
      shouldForceOverwrite: true,
    };
  }
};

export const usePortfolio = ({
  organizationId,
  userId = '',
  onGrossLeaseClick,
  isActive = true,
}) => {
  const name = useUniqueTableName(NAME);

  const {
    portfolioSummaryScreenInWholeNumbers,
    propertyRoleAssignments,
    pastDueColumn: shouldShowPastDueColumn,
    addlPropDetailsMiMoColumns,
    devPhaseDropDown,
    mixedValueComposition,
    dataModalForGrossTotalLeases,
  } = useFlags();

  const { data: portfolios, isFetching: isPortfoliosFetching } =
    usePortfoliosQuery({
      organizationId,
      enabled: isActive,
    });

  // Only the first load blanks the table; a background refetch keeps cached rows visible.
  const isLoading = isPortfoliosFetching && !portfolios?.length;

  const results = useParsePortfolios({
    isLoading,
    portfolios,
    devPhaseDropDown,
  });

  const propertyRoles = useMemo(() => {
    if (!propertyRoleAssignments) return [];

    // FES-1354: each row carries only the roles assigned on that property, so the column set
    // is the union across every row, not whichever row happens to come first.
    const propertyRolesArray = results.flatMap((result) =>
      (result.propertyRoles ?? []).map((role) => role.roleName),
    );
    return [...new Set(propertyRolesArray)].sort((a, b) => a.localeCompare(b));
  }, [results, propertyRoleAssignments]);

  const headers = useHeaders({
    propertyRoles,
    results,
    propertyRoleAssignments,
    pastDueColumn: shouldShowPastDueColumn,
    addlPropDetailsMiMoColumns,
    devPhaseDropDown,
    mixedValueComposition,
  });

  const PROPERTY_PATH_MAP = usePropertyPathMap({
    propertyRoles,
    pastDueColumn: shouldShowPastDueColumn,
    addlPropDetailsMiMoColumns,
    devPhaseDropDown,
    mixedValueComposition,
  });

  const {
    order,
    orderBy,
    handleSortChange,
    filterState,
    filterTypeState,
    dateState,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    initialOrderBy: 'propertyName',
    initialOrder: 'ASC',
    initialFilterState: {
      propertyStatus: ['Active'],
    },
    headers,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    PROPERTY_PATH_MAP,
    searchState,
    filterState,
    filterTypeState,
    dateState,
  });

  const rows = useRows({
    sortedAndFilteredResults,
    portfolioSummaryScreenInWholeNumbers,
    pastDueColumn: shouldShowPastDueColumn,
    isLoading,
    devPhaseDropDown,
    onGrossLeaseClick,
    mixedValueComposition,
    dataModalForGrossTotalLeases,
  });

  // Both memos work together to handle the lifecycle of adding new columns
  // while maintaining a stable drag-and-drop experience

  // Keep saved column order in sync with the live header list so newly-added
  // columns do not snap back to their fallback position.
  const { initialColumnsOrder, shouldForceOverwrite } = useMemo(() => {
    return getStoredColumnOrderState({ headers, name });
  }, [headers, name]);

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    handleTruncateChange,
    truncatedColumns,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const { sum, valuesList } = useMemo(() => {
    const sum = calculateSumOfEachField(sortedAndFilteredResults);
    const valuesList = mapValuesByField(sortedAndFilteredResults);
    return { sum, valuesList };
  }, [sortedAndFilteredResults]);

  const footerRow = useTotalRow({
    results: sortedAndFilteredResults,
    sum,
    valuesList,
    portfolioSummaryScreenInWholeNumbers,
    pastDueColumn: shouldShowPastDueColumn,
    isLoading,
  });
  const KPIData = useKPIData({
    results: sortedAndFilteredResults,
    sum,
    valuesList,
    portfolioSummaryScreenInWholeNumbers,
  });

  const { propertyId, reportId, refreshedAt } = useMemo(() => {
    const result = sortedAndFilteredResults?.length
      ? sortedAndFilteredResults[0]
      : {};

    return {
      propertyId: result.propertyId ?? '',
      reportId: result.reportId ?? '',
      refreshedAt: result.refreshedAt
        ? moment(result.refreshedAt)
            .utc()
            .add(moment().utcOffset(), 'minutes')
            .format('MM/DD/YYYY H:mmA')
        : null,
    };
  }, [sortedAndFilteredResults]);

  const {
    onCSVButtonClick,
    isCSVExportLoading,
    onPDFButtonClick,
    isPDFExportLoading,
  } = useCSVPDFExport({
    organizationId,
    propertyId,
    reportId,
  });

  return {
    KPIData,
    allColumnsHidden,
    columnOptions,
    totalCount: results?.length ?? 0,
    count: rows?.length ?? 0,
    filteredHeaders,
    footerRow,
    handleColumnChange,
    handleSortChange,
    handleTruncateChange,
    isCSVExportLoading,
    isLoading,
    isPDFExportLoading,
    name,
    onCSVButtonClick,
    onPDFButtonClick,
    order,
    orderBy,
    refreshedAt,
    rows,
    selectedColumns,
    truncatedColumns,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    filterState,
    filterTypeState,
    dateState,
    searchState,
  };
};

/**
 * @notes
 *  - will parse the portfolios array to return the correct values for the table
 */
export const useParsePortfolios = ({
  isLoading,
  portfolios = [],
  devPhaseDropDown,
}) => {
  return useMemo(() => {
    if (!portfolios?.length || isLoading) return [];

    return portfolios?.map((row) => {
      const {
        id,
        property,
        refreshedAt,
        numTotalUnits,
        numOccupiedUnits,
        numScheduledMoveIns,
        numScheduledMoveOuts,
        delinquencyPercent,
        delinquencyChargesTotal,
        mtdDeposits,
        pendingDeposits,
        periodToDateConcessions: concessions,
        numWorkOrdersOpen,
        avgWorkOrderDaysOpen,
        approvedScheduledMoveIns,
        mtdMoveIns,
        scheduledMoveOuts,
        mtdMoveOuts,
        lastMonthConcessions,
        lastMonthCharges,
        lastMonthDeposits,
        downUnits,
        numVacantReady,
        numVacantNotReady,
        numNtvCurrentMonth,
        numNtvNextMonth,
        numGrossLeasesApproved,
        numGrossLeasesNotApproved,
        numGrossLeasesTotal,
        numAvailable,
        pastDueCertifications,
        propertyRoles,
      } = row;
      const roleColumns = propertyRoles?.reduce((acc, role) => {
        return {
          ...acc,
          [camelCase(role.roleName)]:
            role.assignedUser && role.assignedUser !== ''
              ? role.assignedUser
              : null,
        };
      }, {});
      const currentOccupancy = numOccupiedUnits / numTotalUnits;
      const netOccupancy =
        (numOccupiedUnits + numScheduledMoveIns - numScheduledMoveOuts) /
        numTotalUnits;
      const vacantUnits = numVacantReady + numVacantNotReady;
      const propertyStatus = property?.propertyStatus?.description ?? '';

      return {
        id,
        propertyName: property?.name ?? '',
        propertyType: property?.propertyType ?? 'Not Provided',
        ...(devPhaseDropDown
          ? { developmentPhase: property?.developmentPhase ?? 'Not Provided' }
          : {}),
        propertyStatus,
        propertyId: property?.id ?? '',
        reportId: property?.reports?.[0]?.id ?? '',
        refreshedAt,
        numTotalUnits,
        vacantUnits,
        numOccupiedUnits,
        currentOccupancy,
        netOccupancy,
        delinquencyPercent,
        delinquencyChargesTotal,
        mtdDeposits,
        pendingDeposits,
        concessions,
        numWorkOrdersOpen: numWorkOrdersOpen ? numWorkOrdersOpen : 0,
        avgWorkOrderDaysOpen: avgWorkOrderDaysOpen ? avgWorkOrderDaysOpen : 0,
        approvedScheduledMoveIns: approvedScheduledMoveIns
          ? approvedScheduledMoveIns
          : 0,
        mtdMoveIns: mtdMoveIns ? mtdMoveIns : 0,
        scheduledMoveOuts: scheduledMoveOuts ? scheduledMoveOuts : 0,
        mtdMoveOuts: mtdMoveOuts ? mtdMoveOuts : 0,
        numScheduledMoveIns,
        numScheduledMoveOuts,
        lastMonthConcessions,
        lastMonthCharges,
        lastMonthDeposits,
        downUnits,
        numNtvCurrentMonth,
        numNtvNextMonth,
        numGrossLeasesApproved,
        numGrossLeasesNotApproved,
        numGrossLeasesTotal,
        numAvailable,
        pastDueCertifications: pastDueCertifications
          ? pastDueCertifications
          : null,
        propertyRoles,
        ...roleColumns,
      };
    });
  }, [portfolios, isLoading, devPhaseDropDown]);
};
const useRows = ({
  sortedAndFilteredResults,
  portfolioSummaryScreenInWholeNumbers,
  pastDueColumn,
  isLoading,
  devPhaseDropDown,
  onGrossLeaseClick,
  mixedValueComposition,
  dataModalForGrossTotalLeases,
}) => {
  return useMemo(() => {
    return sortedAndFilteredResults?.map((result) => {
      const {
        id,
        propertyId,
        propertyName,
        propertyType,
        developmentPhase,
        numTotalUnits,
        vacantUnits,
        numOccupiedUnits,
        numScheduledMoveIns,
        numScheduledMoveOuts,
        delinquencyPercent,
        delinquencyChargesTotal,
        mtdDeposits,
        pendingDeposits,
        concessions,
        numWorkOrdersOpen,
        avgWorkOrderDaysOpen,
        approvedScheduledMoveIns,
        mtdMoveIns,
        scheduledMoveOuts,
        mtdMoveOuts,
        numNtvCurrentMonth,
        numNtvNextMonth,
        numGrossLeasesApproved,
        numGrossLeasesNotApproved,
        numAvailable,
        pastDueCertifications,
        propertyRoles,
        propertyStatus,
      } = result;

      const currentOccupancy = numOccupiedUnits / numTotalUnits;
      const netOccupancy =
        (numOccupiedUnits + numScheduledMoveIns - numScheduledMoveOuts) /
        numTotalUnits;
      const propertyRolesColumns = propertyRoles?.reduce((acc, role) => {
        return {
          ...acc,
          [camelCase(role.roleName)]: {
            value: role.assignedUser,
          },
        };
      }, {});
      const rows = {
        id,
        propertyName: {
          variant: 'link',
          to: propertyId?.length ? `/property/${propertyId}` : null,
          value: propertyName,
        },
        ...(mixedValueComposition
          ? {
              propertyType: {
                variant: 'text',
                value: propertyType,
              },
            }
          : {}),
        ...(devPhaseDropDown
          ? {
              developmentPhase: {
                variant: 'text',
                value: developmentPhase,
              },
            }
          : {}),
        propertyStatus: {
          variant: 'text',
          value: propertyStatus,
        },
        numTotalUnits: {
          variant: 'number',
          value: numTotalUnits,
          numberFormatOptions: { rounded: true },
        },
        numOccupiedUnits: {
          variant: 'number',
          value: numOccupiedUnits,
          numberFormatOptions: { rounded: true },
        },
        vacantUnits: {
          variant: 'number',
          value: vacantUnits,
          numberFormatOptions: { rounded: true },
        },
        currentOccupancy: {
          variant: 'number',
          value: formatPercentage(currentOccupancy),
        },
        netOccupancy: {
          variant: 'number',
          value: formatPercentage(netOccupancy),
        },
        delinquencyPercent: {
          variant: 'number',
          value: formatPercentage(delinquencyPercent),
        },
        delinquencyChargesTotal: {
          variant: 'currency',
          value: delinquencyChargesTotal,
          numberFormatOptions: { rounded: true },
        },
        mtdDeposits: {
          variant: 'currency',
          value: mtdDeposits,
          numberFormatOptions: { rounded: true },
        },
        pendingDeposits: {
          variant: 'currency',
          value: pendingDeposits,
          numberFormatOptions: { rounded: true },
        },
        concessions: {
          variant: 'currency',
          value: concessions,
          numberFormatOptions: { rounded: true },
        },
        approvedScheduledMoveIns: {
          variant: 'number',
          value: approvedScheduledMoveIns ?? 0,
          numberFormatOptions: { rounded: true },
        },
        mtdMoveIns: {
          variant: 'number',
          value: mtdMoveIns ?? 0,
          numberFormatOptions: { rounded: true },
        },
        scheduledMoveOuts: {
          variant: 'number',
          value: scheduledMoveOuts ?? 0,
          numberFormatOptions: { rounded: true },
        },
        mtdMoveOuts: {
          variant: 'number',
          value: mtdMoveOuts ?? 0,
          numberFormatOptions: { rounded: true },
        },
        numWorkOrdersOpen: {
          variant: 'number',
          value: numWorkOrdersOpen ?? 0,
          numberFormatOptions: { rounded: true },
        },
        avgWorkOrderDaysOpen: {
          variant: 'number',
          value: avgWorkOrderDaysOpen ?? 0,
          numberFormatOptions: { rounded: true },
        },
        numNtvCurrentMonth: {
          variant: 'number',
          value: numNtvCurrentMonth ?? 0,
          numberFormatOptions: { rounded: true },
        },
        numNtvNextMonth: {
          variant: 'number',
          value: numNtvNextMonth ?? 0,
          numberFormatOptions: { rounded: true },
        },
        numGrossLeasesApproved: {
          variant:
            onGrossLeaseClick && dataModalForGrossTotalLeases
              ? 'link'
              : 'number',
          value: numGrossLeasesApproved ?? 0,
          numberFormatOptions: { rounded: true },
          ...(onGrossLeaseClick &&
            dataModalForGrossTotalLeases && {
              onClick: () =>
                onGrossLeaseClick(propertyId, propertyName, 'approved'),
            }),
        },
        numGrossLeasesNotApproved: {
          variant:
            onGrossLeaseClick && dataModalForGrossTotalLeases
              ? 'link'
              : 'number',
          value: numGrossLeasesNotApproved ?? 0,
          numberFormatOptions: { rounded: true },
          ...(onGrossLeaseClick &&
            dataModalForGrossTotalLeases && {
              onClick: () =>
                onGrossLeaseClick(propertyId, propertyName, 'not-approved'),
            }),
        },
        numAvailable: {
          variant: 'number',
          value: numAvailable ?? 0,
          numberFormatOptions: { rounded: true },
        },
        ...(pastDueColumn
          ? {
              pastDueCertifications: {
                variant: 'number',
                value: isLoading ? <Spinner small /> : pastDueCertifications,
                numberFormatOptions: { rounded: true },
              },
            }
          : {}),
        ...propertyRolesColumns,
      };
      if (!portfolioSummaryScreenInWholeNumbers) {
        const updatedRows = {};
        Object.keys(rows).forEach((key) => {
          updatedRows[key] = { ...rows[key], numberFormatOptions: undefined };
        });
        return updatedRows;
      }
      return rows;
    });
  }, [
    sortedAndFilteredResults,
    portfolioSummaryScreenInWholeNumbers,
    onGrossLeaseClick,
    pastDueColumn,
    isLoading,
    devPhaseDropDown,
    mixedValueComposition,
    dataModalForGrossTotalLeases,
  ]);
};

const useTotalRow = ({
  sum,
  valuesList,
  portfolioSummaryScreenInWholeNumbers,
  pastDueColumn,
  isLoading,
}) => {
  return useMemo(() => {
    if (!Object.keys(sum).length && !Object.keys(valuesList).length) return {};
    const totalDq =
      (sum.mtdDeposits + sum.pendingDeposits) / sum.delinquencyChargesTotal;
    const rows = {
      propertyName: '',
      numTotalUnits: {
        variant: 'number',
        value: sum.numTotalUnits,
        numberFormatOptions: { rounded: true },
      },
      numOccupiedUnits: {
        variant: 'number',
        value: sum.numOccupiedUnits,
        numberFormatOptions: { rounded: true },
      },
      vacantUnits: {
        variant: 'number',
        value: sum.vacantUnits,
        numberFormatOptions: { rounded: true },
      },
      currentOccupancy: {
        variant: 'number',
        value: formatPercentage(sum.numOccupiedUnits / sum.numTotalUnits),
      },
      netOccupancy: {
        variant: 'number',
        value: formatPercentage(
          weightedAverage(valuesList.netOccupancy, valuesList.numTotalUnits),
        ),
      },
      delinquencyPercent: {
        variant: 'number',
        value: Number.isFinite(totalDq) ? formatPercentage(totalDq) : '---%',
      },
      delinquencyChargesTotal: {
        variant: 'currency',
        value: sum.delinquencyChargesTotal,
        numberFormatOptions: { rounded: true },
      },
      mtdDeposits: {
        variant: 'currency',
        value: sum.mtdDeposits,
        numberFormatOptions: { rounded: true },
      },
      pendingDeposits: {
        variant: 'currency',
        value: sum.pendingDeposits,
        numberFormatOptions: { rounded: true },
      },
      concessions: {
        variant: 'currency',
        value: sum.concessions,
        numberFormatOptions: { rounded: true },
      },
      approvedScheduledMoveIns: {
        variant: 'number',
        value: Number(sum.approvedScheduledMoveIns ?? 0),
        numberFormatOptions: { rounded: true },
      },
      mtdMoveIns: {
        variant: 'number',
        value: Number(sum.mtdMoveIns ?? 0),
        numberFormatOptions: { rounded: true },
      },
      scheduledMoveOuts: {
        variant: 'number',
        value: Number(sum.scheduledMoveOuts ?? 0),
        numberFormatOptions: { rounded: true },
      },
      mtdMoveOuts: {
        variant: 'number',
        value: Number(sum.mtdMoveOuts ?? 0),
        numberFormatOptions: { rounded: true },
      },
      numWorkOrdersOpen: {
        variant: 'number',
        value: Number(sum.numWorkOrdersOpen),
        numberFormatOptions: { rounded: true },
      },
      avgWorkOrderDaysOpen: {
        variant: 'number',
        value: weightedAverage(
          valuesList.avgWorkOrderDaysOpen,
          valuesList.numWorkOrdersOpen,
        ).toFixed(2),
        numberFormatOptions: { rounded: true },
      },
      numNtvCurrentMonth: {
        variant: 'number',
        value: Number(sum.numNtvCurrentMonth),
        numberFormatOptions: { rounded: true },
      },
      numNtvNextMonth: {
        variant: 'number',
        value: Number(sum.numNtvNextMonth),
        numberFormatOptions: { rounded: true },
      },
      numGrossLeasesApproved: {
        variant: 'number',
        value: Number(sum.numGrossLeasesApproved),
        numberFormatOptions: { rounded: true },
      },
      numGrossLeasesNotApproved: {
        variant: 'number',
        value: Number(sum.numGrossLeasesNotApproved),
        numberFormatOptions: { rounded: true },
      },
      numAvailable: {
        variant: 'number',
        value: Number(sum.numAvailable),
        numberFormatOptions: { rounded: true },
      },
      ...(pastDueColumn
        ? {
            pastDueCertifications: {
              variant: 'number',
              value: isLoading ? (
                <Spinner small />
              ) : (
                Number(sum.pastDueCertifications)
              ),
              numberFormatOptions: { rounded: true },
            },
          }
        : {}),
    };
    if (!portfolioSummaryScreenInWholeNumbers) {
      const updatedRows = {};
      Object.keys(rows).forEach((key) => {
        updatedRows[key] = { ...rows[key], numberFormatOptions: undefined };
      });
      return updatedRows;
    }
    return rows;
  }, [
    sum,
    valuesList,
    portfolioSummaryScreenInWholeNumbers,
    pastDueColumn,
    isLoading,
  ]);
};
const useKPIData = ({
  sum,
  valuesList,
  portfolioSummaryScreenInWholeNumbers,
}) => {
  return useMemo(() => {
    const netOccupancyChange =
      sum.numScheduledMoveIns - sum.numScheduledMoveOuts;
    const totalDq =
      (sum.mtdDeposits + sum.pendingDeposits) / sum.delinquencyChargesTotal;
    const values = {
      occupancy: {
        totalUnits: sum.numTotalUnits,
        occupiedUnits: sum.numOccupiedUnits,
        vacantUnits: sum.vacantUnits,
        unOccupiedUnits: sum.numTotalUnits - sum.numOccupiedUnits,
        percentage: formatPercentage(sum.numOccupiedUnits / sum.numTotalUnits),
      },
      netOccupancy: {
        occupiedUnits: sum.numOccupiedUnits + netOccupancyChange,
        unOccupiedUnits:
          sum.numTotalUnits - sum.numOccupiedUnits - netOccupancyChange,
        netOccupancy: sum.netOccupancy,
        totalUnits: sum.numTotalUnits,
        percentage: formatPercentage(
          weightedAverage(valuesList.netOccupancy, valuesList.numTotalUnits),
        ),
        moveIns: sum.numScheduledMoveIns,
        moveOuts: sum.numScheduledMoveOuts,
      },
      income: {
        charges: sum.delinquencyChargesTotal,
        deposits: sum.mtdDeposits,
        pendingDeposits: sum.pendingDeposits,
        depositsPercentOfCharges: Number.isFinite(totalDq)
          ? formatPercentage(totalDq)
          : '---%',
        lastPeriodCharges: sum.lastMonthCharges,
        lastPeriodDeposits: sum.lastMonthDeposits,
        numberFormatOptions: { rounded: true },
      },
      concessions: {
        concessions: sum.concessions,
        lastPeriodConcessions: sum.lastMonthConcessions,
        numberFormatOptions: { rounded: true },
      },
      workOrders: {
        workOrdersOpen: sum.numWorkOrdersOpen,
        avgWorkOrderDaysOpen: `${weightedAverage(
          valuesList.avgWorkOrderDaysOpen,
          valuesList.numWorkOrdersOpen,
        ).toFixed(2)}`,
      },
      noticeToVacates: {
        currentMonth: sum.numNtvCurrentMonth,
        nextMonth: sum.numNtvNextMonth,
      },
      leasing: {
        numGrossLeasesApproved: sum.numGrossLeasesApproved,
        numGrossLeasesNotApproved: sum.numGrossLeasesNotApproved,
        numAvailable: sum.numAvailable,
      },
    };
    if (!portfolioSummaryScreenInWholeNumbers) {
      const updatedValues = {};
      Object.keys(values).forEach((key) => {
        updatedValues[key] = { ...values[key], numberFormatOptions: undefined };
      });
      return updatedValues;
    }
    return values;
  }, [sum, valuesList, portfolioSummaryScreenInWholeNumbers]);
};

const useCSVPDFExport = ({ organizationId, propertyId, reportId }) => {
  const [isCSVExportLoading, setIsCSVExportLoading] = useState(false);
  const [isPDFExportLoading, setIsPDFExportLoading] = useState(false);
  const hasParams =
    organizationId?.length && propertyId?.length && reportId?.length;

  const onCSVButtonClick = useCallback(async () => {
    setIsCSVExportLoading(true);
    await api.getDownload(
      `/${organizationId}/${propertyId}/csv-reports/${reportId}?offset=${moment().utcOffset()}`,
      `Portfolio_Summary_${moment().format('YYYYMMDD')}.csv`,
    );
    setIsCSVExportLoading(false);
  }, [organizationId, propertyId, reportId]);

  const onPDFButtonClick = useCallback(async () => {
    setIsPDFExportLoading(true);
    await api.getDownload(
      `/${organizationId}/${propertyId}/pdf-reports/${reportId}?offset=${moment().utcOffset()}`,
      `Portfolio_Summary_${moment().format('YYYYMMDD')}.pdf`,
    );
    setIsPDFExportLoading(false);
  }, [organizationId, propertyId, reportId]);

  return hasParams
    ? {
        onCSVButtonClick,
        isCSVExportLoading,
        onPDFButtonClick,
        isPDFExportLoading,
      }
    : {};
};

export const useHeaders = ({
  propertyRoles,
  results,
  propertyRoleAssignments,
  pastDueColumn,
  addlPropDetailsMiMoColumns,
  devPhaseDropDown,
  mixedValueComposition,
}) => {
  const intl = useIntl();
  const filterOptions = useBuildFilterOptions({
    results,
    propertyRoles,
    devPhaseDropDown,
    mixedValueComposition,
  });
  return useMemo(() => {
    const headers = getHeaders({
      propertyRoles,
      propertyRoleAssignments,
      pastDueColumn,
      addlPropDetailsMiMoColumns,
      devPhaseDropDown,
      mixedValueComposition,
      intl,
    });
    const headersWithFilterOptions = headers.map((header) => ({
      ...header,
      filterOptions: header.filterOptions ?? filterOptions[header.id] ?? null,
    }));
    return headersWithFilterOptions;
  }, [
    propertyRoles,
    filterOptions,
    propertyRoleAssignments,
    pastDueColumn,
    addlPropDetailsMiMoColumns,
    devPhaseDropDown,
    mixedValueComposition,
    intl,
  ]);
};

export const usePropertyPathMap = ({
  propertyRoles,
  propertyRoleAssignments,
  pastDueColumn,
  addlPropDetailsMiMoColumns,
  devPhaseDropDown,
  mixedValueComposition,
}) => {
  return useMemo(
    () =>
      getPropertyPathMap({
        propertyRoles,
        propertyRoleAssignments,
        pastDueColumn,
        addlPropDetailsMiMoColumns,
        devPhaseDropDown,
        mixedValueComposition,
      }),
    [
      propertyRoles,
      propertyRoleAssignments,
      pastDueColumn,
      addlPropDetailsMiMoColumns,
      devPhaseDropDown,
      mixedValueComposition,
    ],
  );
};

export const useBuildFilterOptions = ({
  results,
  propertyRoles,
  devPhaseDropDown,
  mixedValueComposition,
}) => {
  return useMemo(() => {
    const options = propertyRoles.reduce((acc, role) => {
      return {
        ...acc,
        [camelCase(role)]: [],
      };
    }, {});

    if (results) {
      results.forEach((result) => {
        propertyRoles.forEach((role) => {
          if (result[camelCase(role)]) {
            options[camelCase(role)].push(result[camelCase(role)]);
          }
        });
      });
    }

    processTableFilterOptions(options);

    // Add development phase options if feature flag is enabled
    if (devPhaseDropDown) {
      options.developmentPhase = extractUniqueDevelopmentPhases(results || []);
    }

    // Add property type options if feature flag is enabled
    if (mixedValueComposition) {
      options.propertyType = extractUniquePropertyTypes(results || []);
    }

    return options;
  }, [results, propertyRoles, devPhaseDropDown, mixedValueComposition]);
};
